import type { DispatchAutoFixLoopIterationResult } from '@/lib/auto-fix-loop/dispatch';

import { formatAutoFixPauseText } from '@/lib/auto-fix-allowance/model';

export const AUTO_FIX_RETRY_FAILED_MESSAGE = 'Auto-fix could not be retried.';

/**
 * The API answers a retry it accepted with the dispatch's outcome, including
 * ones that start no run; those read as a refusal, never the dispatch's
 * internal error text. `null` when a run is starting, already underway, or
 * waits for the head's CI or for other runs to free auto-fix usage, which dispatches it once it
 * can. A paused dispatch explains itself from its pause instead.
 */
const RETRY_OUTCOME_REFUSALS: Record<
  Exclude<DispatchAutoFixLoopIterationResult['outcome'], 'paused'>,
  string | null
> = {
  dispatched: null,
  already_running: null,
  no_issues: 'No open issues or failing checks left to retry',
  nothing_to_do: 'No open issues or failing checks left to retry',
  waiting_for_ci: null,
  waiting_for_allowance: null,
  loop_off: 'Auto-fix is off',
  disabled: "Auto-fix can't run on this pull request",
  failed: 'The new run could not start',
  retry_cap_reached: 'The new run could not start',
  needs_environment_setup: "Set up the repository's environment, then retry",
};

/**
 * What an accepted retry that waits for the organization's auto-fix usage says: runs on other PRs
 * reserve all its plan has left, and a re-check starts it once they free some (or pauses it if
 * their fixes use it up). A plan-limit pause keeps showing until then, so without this a Resume
 * would look like it did nothing.
 */
export const AUTO_FIX_RETRY_WAITING_NOTICE = {
  title: 'Waiting for other auto-fix runs to finish',
  description: 'Auto-fix tries again in a few minutes.',
} as const;

/** What an accepted retry waits for, said once it is sent; null when nothing needs saying. */
export function autoFixRetryNotice(
  dispatch: DispatchAutoFixLoopIterationResult | null | undefined,
): typeof AUTO_FIX_RETRY_WAITING_NOTICE | null {
  return dispatch?.outcome === 'waiting_for_allowance' ? AUTO_FIX_RETRY_WAITING_NOTICE : null;
}

/**
 * Why a retry the loop API accepted starts no run, for the PR page, the fix session page and
 * the action page alike. An outcome this client does not know, or none, keeps the retry accepted.
 */
export function autoFixRetryRefusal(
  dispatch: DispatchAutoFixLoopIterationResult | null | undefined,
  now: Date,
): string | null {
  if (!dispatch) {
    return null;
  }
  if (dispatch.outcome === 'paused') {
    // "Get more usage" is already in view wherever a pause shows, so the refusal only says why.
    return formatAutoFixPauseText(dispatch.pause, now);
  }
  return Object.hasOwn(RETRY_OUTCOME_REFUSALS, dispatch.outcome)
    ? RETRY_OUTCOME_REFUSALS[dispatch.outcome]
    : null;
}
